// Shared app chrome: a top bar with the app title and a hamburger menu.
//
// The header reads "Couples Games - <game>" (or just "Couples Games" on the
// apex). The menu's Home/Profile/Stats items link out to the apex domain so
// profile + stats live in exactly one place and sign-in is shared.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../store/auth.js";
import { config } from "../lib/config.js";

interface AppShellProps {
  /** The game's display name (e.g. "Corners"). Omit on the apex landing page. */
  appName?: string;
  children: ReactNode;
}

export function AppShell({ appName, children }: AppShellProps): JSX.Element {
  const { session, profile, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const name = profile?.name ?? profile?.email ?? (session ? "Signed in" : null);
  const title = appName ? `Couples Games - ${appName}` : "Couples Games";

  return (
    <div className="min-h-dvh bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-4">
          <a
            href="/"
            className="font-semibold tracking-tight text-slate-100 hover:text-sky-400 transition"
          >
            {title}
          </a>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label="Open menu"
              aria-expanded={open}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 transition"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                {open ? (
                  <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                ) : (
                  <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                )}
              </svg>
            </button>

            {open && (
              <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-xl">
                {name && (
                  <div className="border-b border-slate-800 px-4 py-3">
                    <div className="truncate text-sm font-medium text-slate-100">{name}</div>
                    {profile?.email && (
                      <div className="truncate text-xs text-slate-400">{profile.email}</div>
                    )}
                  </div>
                )}
                <div className="p-1">
                  <MenuLink href={config.landingOrigin}>Home</MenuLink>
                  <MenuLink href={`${config.landingOrigin}/profile`}>Profile</MenuLink>
                  <MenuLink href={`${config.landingOrigin}/stats`}>Stats</MenuLink>
                </div>
                <div className="border-t border-slate-800 p-1">
                  {session ? (
                    <MenuItem
                      onClick={() => {
                        setOpen(false);
                        void logout();
                      }}
                    >
                      Sign out
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onClick={() => {
                        setOpen(false);
                        void login(window.location.pathname);
                      }}
                    >
                      Sign in with Google
                    </MenuItem>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

function MenuItem({ children, onClick }: { children: ReactNode; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800 hover:text-white transition"
    >
      {children}
    </button>
  );
}

function MenuLink({ href, children }: { href: string; children: ReactNode }): JSX.Element {
  return (
    <a
      href={href}
      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800 hover:text-white transition"
    >
      {children}
    </a>
  );
}
